// Generated by tutorials/record.cjs from tutorials/articles/h52-items-and-recipes.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h52 --text-only` (wording) or
// `node tutorials/record.cjs h52` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'items-and-recipes',
  catalogId: 'h52',
  title: 'Items and recipes',
  summary: 'Items are what you serve and recipes are how you make them. Find and read an item and its recipe, then add a recipe and the menu item that serves it.',
  audience: 'Menu planners and dietary managers who keep the food data',
  area: 'food-data',
  learn: [
    'Find an item, and see which items are on today\'s menu cycle',
    'Read an item: sizes, food groups, nutrition, allergy groups and Auto Item Mods',
    'Find a recipe and read its ingredients, instructions and nutrients',
    'Add a recipe with ingredients, steps and serving sizes',
    'Add the menu item for a recipe, and change an item or recipe',
  ],
  keywords: [
    'item',
    'items',
    'recipe',
    'recipes',
    'food data',
    'ingredient',
    'ingredients',
    'menu item',
    'new item',
    'add item',
    'item size',
    'serving size',
    'portion',
    'food group',
    'allergy group',
    'allergen',
    'nutrition',
    'nutrients',
    'nutrition from',
    'yield',
    'base yield',
    'instructions',
    'recipe number',
    'menu description',
    'readiness',
    'things missing',
    'pureed',
    'dri',
  ],
  steps: [
    {
      id: 'what',
      heading: 'Items and recipes',
      text: 'An item is anything you serve: CHICKEN POT PIE, COFFEE, a packet of ketchup. Its sizes, food groups and allergy groups decide how it is portioned, and which adjustments, dislikes and food allergies catch it.\n\nA recipe is how to make it: the ingredients and amounts, the steps, and how many servings it makes. The ingredients are items too.\n\nEvery item you put on a menu should be linked to a recipe. The item gets its nutrition from that recipe, and Dietech works out the recipe\'s nutrients from the food data of its ingredients.',
      textOnly: true,
    },
    {
      id: 'items',
      part: 'Find an item',
      heading: 'Open Items',
      text: 'In the menu, select Food Data, then Items. Every item is listed by Description, with its Menu Display Name, whether it is a condiment, whether it is hidden from selective menus (Hide SM), and its calories.\n\nThe number at the bottom counts the items listed.',
      media: {
        image: {
          src: '/help/items-and-recipes/items.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Items list: APPLE CRISP, BEEF STEW, BUTTERED CARROTS, CHICKEN POT PIE and others, with Menu Display Name, Condiment, Hide SM and Calories, and 42 items at the bottom.',
      },
    },
    {
      id: 'search',
      part: 'Find an item',
      heading: 'Search',
      text: 'Type part of a name in Search items. The list keeps the items whose Description or Menu Display Name has it.\n\nThe ingredients your recipes use are items too, so they show up here with your menu items.',
      media: {
        image: {
          src: '/help/items-and-recipes/search.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/items-and-recipes/search.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.9,
        },
        alt: 'Search items holds chicken, and the list shows the chicken items: CHICKEN BREAST BAKED, CHICKEN BROTH LOW SODIUM, CHICKEN DICED COOKED, CHICKEN NOODLE SOUP, CHICKEN POT PIE and CHICKEN SALAD.',
      },
    },
    {
      id: 'on-menu',
      part: 'Find an item',
      heading: 'See what is on the menu',
      text: 'Select Highlight Active Menu Cycle Items. The items on the menu cycle in use today turn green.\n\nThen select Menu Cycle Only to list just those items. Select it again to see every item.',
      media: {
        image: {
          src: '/help/items-and-recipes/on-menu.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/items-and-recipes/on-menu.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.1,
        },
        alt: 'Highlight Active Menu Cycle Items and Menu Cycle Only selected: the list shows the 20 items on today\'s menu cycle, in green.',
      },
    },
    {
      id: 'open',
      part: 'Read an item',
      heading: 'Open an item',
      text: 'Select the item\'s row, then Open, or double-click the row.\n\nThe item has tabs: General, Production, Nutrition, Allergies and Auto Item Mods. At the top of General, a line says Ready: nothing is missing, or lists what is missing and what that breaks.\n\nUnder Item Details, Description is the item\'s name in Dietech. Menu Description is the name printed on menus. Category is Food, Beverage or Supplement, and Class is Cold or Hot.',
      media: {
        image: {
          src: '/help/items-and-recipes/open.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/items-and-recipes/open.mp4',
          width: 1280,
          height: 748,
          durationSec: 5.5,
        },
        alt: 'CHICKEN POT PIE on the General tab: Ready: nothing is missing, Item Details with Description and Menu Description CHICKEN POT PIE, Food, Hot, Cost 1.85 and Prep Instructions.',
      },
    },
    {
      id: 'sizes',
      part: 'Read an item',
      heading: 'Item sizes',
      text: 'Item Sizes lists the amounts the item is served in, up to 9. The green check marks the default size.\n\nA check under a portion, such as SM or LG, marks the size used for that portion. Each portion can be on one size only.\n\nNutrition analysis uses the size to portion the recipe, so an item with no sizes shows no nutrition.',
      media: {
        image: {
          src: '/help/items-and-recipes/sizes.webp',
          width: 800,
          height: 328,
        },
        alt: 'Item Sizes for CHICKEN POT PIE: 1 ea the default, 0.5 ea checked under SM and 1.5 ea checked under LG, with Add Size.',
      },
    },
    {
      id: 'groups',
      part: 'Read an item',
      heading: 'Flags and food groups',
      text: 'Flags & Options are switches such as Condiment and Hide from Selective Menu.\n\nFood Groups says what kind of food the item is, here Entree > Poultry > Chicken. An item can be in several. Adjustments and dislikes by food group find items through these groups, and production tallies filtered by food group use them, so an item with none is missed.\n\nTray Ticket Display sets the item\'s color on the tray ticket.',
      media: {
        image: {
          src: '/help/items-and-recipes/groups.webp',
          width: 800,
          height: 856,
        },
        alt: 'Flags & Options with every box unchecked, Food Groups with Entree > Poultry > Chicken, and Tray Ticket Display.',
      },
    },
    {
      id: 'nutrition',
      part: 'Read an item',
      heading: 'Nutrition comes from the recipe',
      text: 'Select Nutrition. Nutrition from says where the item\'s nutrition comes from: here the recipe Chicken Pot Pie (1042). Open recipe opens it.\n\nAn item with no recipe has no nutrition: nutrition analysis shows it as having none.\n\nNutrition by Consistency or Item Type covers another texture: when the item is served Pureed, its nutrition and allergens come from the pureed Chicken Pot Pie recipe.',
      media: {
        image: {
          src: '/help/items-and-recipes/nutrition.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/items-and-recipes/nutrition.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.5,
        },
        alt: 'The Nutrition tab: Nutrition from Recipe, Recipe Chicken Pot Pie (1042), Open recipe, and a Pureed row taking its nutrition from Recipe: Chicken Pot Pie.',
      },
    },
    {
      id: 'allergies',
      part: 'Read an item',
      heading: 'Allergy groups',
      text: 'Select Allergies. Allergy groups say which allergens the item has. Without them, residents with that food allergy are not kept from getting it, and selective menus do not flag it.\n\nGroups with a lock come from the ingredients of the linked recipe: here Milk and Wheat/Gluten. Change those on the recipe. Add an allergy group here only for one the recipe does not bring.',
      media: {
        image: {
          src: '/help/items-and-recipes/allergies.webp',
          width: 800,
          height: 236,
        },
        alt: 'The Allergies tab: Milk and Wheat/Gluten, each with a lock, and Add an allergy group.',
      },
    },
    {
      id: 'aim',
      part: 'Read an item',
      heading: 'Auto Item Mods',
      text: 'Select Auto Item Mods. Each rule says what a diet or texture gets instead of this item. Here Renal gets CHICKEN BREAST BAKED, and Mechanical Soft and Pureed get the pot pie in that texture.\n\nSee Auto Item Mods (AIM) to add and change rules.',
      media: {
        image: {
          src: '/help/items-and-recipes/aim.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/items-and-recipes/aim.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.3,
        },
        alt: 'The Auto Item Mods tab for CHICKEN POT PIE: Renal changes to CHICKEN BREAST BAKED, Mechanical Soft and Pureed change to the same item in that consistency.',
      },
    },
    {
      id: 'recipes',
      part: 'Read a recipe',
      heading: 'Find a recipe',
      text: 'In the menu, select Food Data, then Recipes. Each recipe shows its Number, its Name (with its item type and texture), Rating, Portion and Menu Name. A check under FAC marks your facility\'s own recipes; the others come from PSI.\n\nType in Search recipes to look in names, menu names and numbers. Category and On Menu narrow the list: On Menu keeps the recipes used on the menu.',
      media: {
        image: {
          src: '/help/items-and-recipes/recipes.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/items-and-recipes/recipes.mp4',
          width: 1280,
          height: 748,
          durationSec: 5.4,
        },
        alt: 'The Recipes list with pot pie in Search recipes: 1042 Chicken Pot Pie (Entree) and 1042P Chicken Pot Pie (Entree - Pureed).',
      },
    },
    {
      id: 'recipe',
      part: 'Read a recipe',
      heading: 'Open the recipe',
      text: 'Select the recipe, then Open, or double-click it.\n\nOverview shows the recipe at a glance. Base Yield is how many servings the recipe makes; nutrients per serving are worked out from it. Serving Sizes, further down, are the sizes a serving comes in.',
      media: {
        image: {
          src: '/help/items-and-recipes/recipe.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/items-and-recipes/recipe.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.1,
        },
        alt: 'Chicken Pot Pie #1042 on Overview: Basic Information (Menu Name, Recipe Number, Portion 1 each, Item Type Entree), Yield Information with Base Yield 24, and Cooking Information.',
      },
    },
    {
      id: 'ingredients',
      part: 'Read a recipe',
      heading: 'Ingredients',
      text: 'Select Ingredients. Each ingredient is listed in order with its amount, such as Pound: 4 1/2 for the whole recipe. Amounts show as kitchen fractions (1/3, 2/3, 1 1/2) where they are one. In brackets is how it is prepared, with its preparation waste. A note under an ingredient, in italics, says how to prepare it.',
      media: {
        image: {
          src: '/help/items-and-recipes/ingredients.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Ingredients tab: 1 CHICKEN DICED COOKED, Pound: 4 1/2, 2 PEAS AND CARROTS FROZEN, Pound: 2, 3 ONION DICED (Diced) Prep waste: 10%, Ounce: 12, and more.',
      },
    },
    {
      id: 'instructions',
      part: 'Read a recipe',
      heading: 'Instructions',
      text: 'Select Instructions for the steps, in order.',
      media: {
        image: {
          src: '/help/items-and-recipes/instructions.webp',
          width: 1280,
          height: 470,
        },
        clip: {
          src: '/help/items-and-recipes/instructions.mp4',
          width: 1280,
          height: 470,
          durationSec: 2.4,
        },
        alt: 'The Instructions tab with six numbered steps, from melting the butter to baking at 375 F for 30 to 35 minutes.',
      },
    },
    {
      id: 'recipe-nutrition',
      part: 'Read a recipe',
      heading: 'Nutrients per serving',
      text: 'Select Nutrition. Calculated is each nutrient in one serving, worked out from the ingredients and the base yield. Override, when it has a value, is used in place of the calculated one.\n\nThe box at the top picks a DRI group; it starts at your facility\'s default. DRI % (meal) compares the serving with one meal, a third of the day\'s value. Show all nutrients lists every nutrient.\n\nWhen an ingredient has no nutrient data, a note above the table names it and says the values are low. Add the data to that ingredient, and the recipe follows.',
      media: {
        image: {
          src: '/help/items-and-recipes/recipe-nutrition.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/items-and-recipes/recipe-nutrition.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.5,
        },
        alt: 'Nutrients per serving (base yield 24) for Women 71+: Calories 410 at 77% of a meal, Protein 24, Carbohydrate 32, Total Fat 20, Sodium 620 at 124%, Dietary Fiber 3, Calcium 70 and Potassium 390.',
      },
    },
    {
      id: 'recipe-allergies',
      part: 'Read a recipe',
      heading: 'Allergens',
      text: 'Select Allergies. The recipe\'s allergens are found from its ingredients\' allergy groups: here Milk, from the butter and milk, and Wheat/Gluten, from the flour and crust. An item linked to the recipe shows them, locked, on its Allergies tab.',
      media: {
        image: {
          src: '/help/items-and-recipes/recipe-allergies.webp',
          width: 1280,
          height: 186,
        },
        clip: {
          src: '/help/items-and-recipes/recipe-allergies.mp4',
          width: 1280,
          height: 186,
          durationSec: 2.4,
        },
        alt: 'The Allergies tab: Allergens (auto-detected from ingredients) Milk and Wheat/Gluten.',
      },
    },
    {
      id: 'new-recipe',
      part: 'Add a recipe',
      heading: 'Start a new recipe',
      text: 'On Recipes, select New. Fill in Basic Info: Name (needed), Menu Name, your Recipe Number if you use them, and Portion. Under Yield Information, type the Base Yield: how many servings the recipe makes.\n\nIngredients, steps and serving sizes come after the first save.',
      media: {
        image: {
          src: '/help/items-and-recipes/new-recipe.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/items-and-recipes/new-recipe.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'A new recipe on Basic Info: Name and Menu Name Turkey Tetrazzini, Portion 1 cup, Base Yield 24, and Serving Sizes saying Save the recipe first, then add serving sizes.',
      },
    },
    {
      id: 'recipe-saved',
      part: 'Add a recipe',
      heading: 'Save, then Edit',
      text: 'Select Save at the bottom. The recipe opens, with FAC beside its name: it is your facility\'s own.\n\nSelect Edit to add the rest.',
      media: {
        image: {
          src: '/help/items-and-recipes/recipe-saved.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/items-and-recipes/recipe-saved.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.7,
        },
        alt: 'The saved recipe Turkey Tetrazzini on Overview, marked FAC, with Ingredients (0) and Instructions (0), and the pointer on Edit.',
      },
    },
    {
      id: 'add-ingredient',
      part: 'Add a recipe',
      heading: 'Add an ingredient',
      text: 'Select Ingredients, then Add Ingredient. In the new row, type a few letters of the ingredient and pick it from the list.\n\nPrep Type and Prep Waste % are there if you need them. Advanced holds more: a child recipe in place of an item, a menu name, prep and pull days, and a note on how to prepare it.',
      media: {
        image: {
          src: '/help/items-and-recipes/add-ingredient.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/items-and-recipes/add-ingredient.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'Ingredients (1) on the recipe being edited: row 1 holds turk, with TURKEY SANDWICH and TURKEY DICED COOKED offered, and the pointer on TURKEY DICED COOKED.',
      },
    },
    {
      id: 'amount',
      part: 'Add a recipe',
      heading: 'Give it an amount',
      text: 'Select + Add Amount. Type the Qty for the whole recipe and pick the Unit. The Qty can be a fraction, such as 2/3 or 1 1/2, or a decimal. An ingredient can have up to five amounts.\n\nEach change to an ingredient is saved as you make it: Ingredient saved shows at the top. Add every ingredient the same way. Nutrients are worked out from these amounts, so give each ingredient one.',
      media: {
        image: {
          src: '/help/items-and-recipes/amount.webp',
          width: 1280,
          height: 348,
        },
        clip: {
          src: '/help/items-and-recipes/amount.mp4',
          width: 1280,
          height: 348,
          durationSec: 6,
        },
        alt: 'Row 1 TURKEY DICED COOKED with Qty 5 and Unit Pound, and Ingredient saved at the top right.',
      },
    },
    {
      id: 'steps',
      part: 'Add a recipe',
      heading: 'Add the steps',
      text: 'Select Instructions, then Add Step, and type the step. It is saved when you click out of the box. Add a step for each part of the method.\n\nThe arrows move a step up or down; you can also drag its number.',
      media: {
        image: {
          src: '/help/items-and-recipes/steps.webp',
          width: 1280,
          height: 256,
        },
        clip: {
          src: '/help/items-and-recipes/steps.mp4',
          width: 1280,
          height: 256,
          durationSec: 6,
        },
        alt: 'The Instructions tab with step 1: Cook and drain the noodles.',
      },
    },
    {
      id: 'serving-size',
      part: 'Add a recipe',
      heading: 'Add a serving size and save',
      text: 'Select Basic Info. Under Serving Sizes, select Add Size and give the Size and Unit of one serving, here 1 Cup.\n\nSelect Save. Basic Info, serving sizes and categories are saved with it, and the recipe opens. Its Allergies tab now shows what its ingredients bring.',
      media: {
        image: {
          src: '/help/items-and-recipes/serving-size.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/items-and-recipes/serving-size.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'Serving Sizes on the recipe being edited: Size 1, Unit Cup, and the pointer on Save.',
      },
    },
    {
      id: 'change-recipe',
      part: 'Add a recipe',
      heading: 'Changing a recipe',
      text: 'Open the recipe and select Edit. Changes to ingredients and steps are saved as you make them. Basic Info, serving sizes, categories and nutrient overrides are saved with Save; Cancel leaves them as they were.\n\nA recipe from PSI cannot be changed here: Copy to my recipes makes your own copy to change.',
      textOnly: true,
    },
    {
      id: 'new-item',
      part: 'Add the item for a recipe',
      heading: 'Start a new item',
      text: 'To put the recipe on a menu, it needs an item. On Items, select New.\n\nType the Description. Menu Description fills in with it; change it if the menu should say something else. Pick the Category and Class.\n\nThe line at the top counts what is still missing. Select it to see each thing, what it breaks, and a Fix button that takes you there.',
      media: {
        image: {
          src: '/help/items-and-recipes/new-item.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/items-and-recipes/new-item.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'A new item: Description and Menu Description TURKEY TETRAZZINI, Food and Hot picked, and 5 things missing opened at the top: no allergy groups, food groups, main production area, sizes, recipe or ingredient link.',
      },
    },
    {
      id: 'item-size',
      part: 'Add the item for a recipe',
      heading: 'Add a size',
      text: 'Under Item Sizes, select Add Size. Type the Size and pick the Unit, here 1 Cup, check Default size, and select OK.',
      media: {
        image: {
          src: '/help/items-and-recipes/item-size.webp',
          width: 944,
          height: 550,
        },
        clip: {
          src: '/help/items-and-recipes/item-size.mp4',
          width: 944,
          height: 550,
          durationSec: 6,
        },
        alt: 'The Add Item Size box: Size 1, Unit Cup, Default size checked, and the SM, LG, DBL and HALF portion boxes.',
      },
    },
    {
      id: 'item-food-group',
      part: 'Add the item for a recipe',
      heading: 'Fix what is missing: a food group',
      text: 'In the list at the top, select Fix beside No food groups. Dietech takes you to Food Groups. Pick the group under Add a food group; typing finds it.\n\nThat line leaves the list as soon as the item has a food group.',
      media: {
        image: {
          src: '/help/items-and-recipes/item-food-group.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/items-and-recipes/item-food-group.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'Food Groups on the new item with Entree > Poultry > Turkey added.',
      },
    },
    {
      id: 'item-production',
      part: 'Add the item for a recipe',
      heading: 'Pick its production area',
      text: 'Select Production and pick the Main Production Area, where the item is made. Production tallies list items by it.\n\nPrep and pull areas follow the recipe, so they are greyed out for an item linked to one.',
      media: {
        image: {
          src: '/help/items-and-recipes/item-production.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/items-and-recipes/item-production.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'The Production tab of the new item: Main Production Area Hot Line, and Temperatures & Timing.',
      },
    },
    {
      id: 'item-recipe',
      part: 'Add the item for a recipe',
      heading: 'Link the recipe',
      text: 'Select Nutrition. Under Nutrition from, pick Recipe. Then pick the recipe under Recipe; typing finds it.\n\nThe item now takes its nutrition from Turkey Tetrazzini, and its allergy groups from the recipe\'s ingredients.',
      media: {
        image: {
          src: '/help/items-and-recipes/item-recipe.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/items-and-recipes/item-recipe.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'The Nutrition tab of the new item: Nutrition from Recipe, Recipe Turkey Tetrazzini, and Open recipe.',
      },
    },
    {
      id: 'item-saved',
      part: 'Add the item for a recipe',
      heading: 'Save',
      text: 'Select Save. Item saved shows at the top, the item gets its ID, and the line on General says Ready: nothing is missing. The allergy groups came from the recipe.\n\nThe item is ready to put on a menu cycle.',
      media: {
        image: {
          src: '/help/items-and-recipes/item-saved.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/items-and-recipes/item-saved.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'TURKEY TETRAZZINI, ID 2101, saved: Ready: nothing is missing, with its 1 c default size and the Entree > Poultry > Turkey food group.',
      },
    },
    {
      id: 'change-item',
      part: 'Add the item for a recipe',
      heading: 'Changing an item',
      text: 'Open the item and change what you need on any tab. A dot on a tab marks unsaved changes, and the bar at the bottom says Unsaved changes. Save saves the whole item; Discard puts it back as it was saved.\n\nIf you leave with unsaved changes, Dietech asks first: Save & Leave, or Discard. If someone else saved the item while you had it open, Dietech says so and offers to reload it.',
      textOnly: true,
    },
  ],
  tips: [
    'New From Existing, on an item, opens a copy to change before you save it. On the Recipes list, Copy makes a full copy of a recipe, ingredients and all, with the same name and number: change them after.',
    'The columns button above the Items list adds more columns, such as Food Group, Allergy Group and Production Area.',
    'Search at the top of the screen finds items and recipes from any page.',
    'The readiness line on an item\'s General tab follows your changes as you make them, so you can see a fix work before you save.',
  ],
  notes: [
    'An item or recipe that menus, recipes or other records still use cannot be deleted. Dietech tells you where it is used.',
    'A recipe from PSI is kept up to date by the Dietech data update, so it cannot be changed here. Open it, select Edit, then Copy to my recipes to make your own copy to change.',
    'When other facilities share an item with you, a note on the item says which settings are saved for your facility only. Everything else changes for every facility.',
    'Adding, changing and deleting items and recipes each need the right role. If New, Edit or Save is missing or greyed out, ask your administrator.',
  ],
  related: [
    'auto-item-mods',
    'changing-menu-items',
    'menu-cycles',
    'menu-analysis',
    'adjustments',
    'allergies-likes-dislikes',
  ],
  recorded: '2026-11-03',
};

export default article;
